import React, { useMemo, useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer,
} from 'recharts';

// AOC-style "Percentage Graph": step lines of a strike's Volume / OI / OI change as a %
// of the strongest strike, with end-value badges, a threshold line and a crosshair.

const SERIES = [
  { key: 'Volume_pct',   id: 'volume',   name: 'Volume', color: '#2f55d4' },
  { key: 'OI_pct',       id: 'oi',       name: 'OI',     color: '#9b30d9' },
  { key: 'OIChange_pct', id: 'oichange', name: 'OIChng', color: '#f26b21' },
];

const THRESHOLD = 75;
const CHART_HEIGHT = 520;
const MARGIN = { top: 16, right: 8, bottom: 8, left: 8 };
const X_AXIS_HEIGHT = 34;
const Y_AXIS_WIDTH = 64;
const IST_OFFSET_SEC = 330 * 60;
const BADGE_GAP = 23;                         // px between stacked axis badges
const SESSION_START_SEC = 9 * 3600 + 15 * 60;  // 09:15 IST
const SESSION_END_SEC = 15 * 3600 + 30 * 60;   // 15:30 IST
const FONT = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";

const C = {
  bg: '#131313',
  header: '#1b1b1b',
  grid: '#262626',
  text: '#e8e8e8',
  muted: '#8a8a8a',
  threshold: '#4caf50',
};

// Epoch seconds → IST clock parts (independent of the viewer's timezone).
const istParts = (ts) => {
  const d = new Date((ts + IST_OFFSET_SEC) * 1000);
  return { h: d.getUTCHours(), m: d.getUTCMinutes(), s: d.getUTCSeconds() };
};
const fmtClock = (ts, withSeconds) => {
  const { h, m, s } = istParts(ts);
  const hh = h % 12 || 12;
  const mm = String(m).padStart(2, '0');
  const ss = withSeconds ? `:${String(s).padStart(2, '0')}` : '';
  return `${hh}:${mm}${ss} ${h >= 12 ? 'PM' : 'AM'}`;
};

// Pill drawn on the right-hand value axis.
// `dy` shifts the pill off its line so nearby badges don't overlap.
const AxisBadge = ({ viewBox, value, fill, textColor = '#fff', dy = 0, format = (v) => Number(v).toFixed(2) }) => {
  if (!viewBox) return null;
  const x = viewBox.x + viewBox.width + 2;
  const y = viewBox.y + dy;
  return (
    <g>
      <rect x={x} y={y - 11} width={Y_AXIS_WIDTH - 4} height={22} rx={3} fill={fill} />
      <text x={x + (Y_AXIS_WIDTH - 4) / 2} y={y + 5} textAnchor="middle" fill={textColor} fontSize={13} fontWeight={600}>
        {format(value)}
      </text>
    </g>
  );
};

// Time pill drawn over the x-axis under the crosshair.
const TimeBadge = ({ viewBox, label }) => {
  if (!viewBox) return null;
  const w = 88;
  const x = viewBox.x - w / 2;
  const y = viewBox.y + viewBox.height + 2;
  return (
    <g>
      <rect x={x} y={y} width={w} height={26} rx={2} fill="#f2f2f2" />
      <text x={viewBox.x} y={y + 18} textAnchor="middle" fill="#111" fontSize={13} fontWeight={600}>{label}</text>
    </g>
  );
};

const XTick = ({ x, y, payload }) => {
  const { m } = istParts(payload.value);
  const strong = m === 0;
  return (
    <text x={x} y={y + 16} textAnchor="middle" fill={C.text} fontSize={13} fontWeight={strong ? 700 : 400}>
      {fmtClock(payload.value, false)}
    </text>
  );
};

const HoverTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ color: C.text, fontSize: 13, lineHeight: '20px', pointerEvents: 'none', textShadow: '0 0 4px #000' }}>
      <div style={{ fontWeight: 700, marginBottom: 6 }}>{fmtClock(label, true)}</div>
      {payload.map((p) => (
        <div key={p.dataKey}>{p.name}: {Number(p.value).toFixed(2)}</div>
      ))}
    </div>
  );
};

const IconButton = ({ active, title, onClick, children, ...rest }) => (
  <button
    type="button"
    title={title}
    onClick={onClick}
    {...rest}
    style={{
      background: 'none', border: 'none', padding: 4, cursor: 'pointer', lineHeight: 0,
      color: active ? '#5b8def' : C.text,
    }}
  >
    {children}
  </button>
);

// `sessionStart` / `sessionEnd` (epoch seconds) override the default NSE 09:15–15:30 axis,
// e.g. for MCX commodities.
const OIPercentageGraph = ({ data = [], loading = false, label = '', markerTime = '', sessionStart, sessionEnd }) => {
  const [visible, setVisible] = useState({ volume: true, oi: true, oichange: true });
  const [view, setView] = useState('chart');
  const [hover, setHover] = useState(null); // { ts, value }

  const lines = SERIES.filter((s) => visible[s.id]);
  const rows = useMemo(
    () => data.filter((d) => Number.isFinite(d.Timestamp)).sort((a, b) => a.Timestamp - b.Timestamp),
    [data]
  );
  const last = rows[rows.length - 1];

  // Fixed AOC-like scale (-10 … 120), widened in steps of 10 when values fall outside it.
  const [yMin, yMax] = useMemo(() => {
    const vals = rows.flatMap((d) => lines.map((l) => d[l.key] ?? 0));
    const lo = Math.min(-10, Math.floor((Math.min(...vals, 0) - 5) / 10) * 10);
    const hi = Math.max(120, Math.ceil((Math.max(...vals, 0) + 5) / 10) * 10);
    return [lo, hi];
  }, [rows, lines]);
  const yTicks = useMemo(() => {
    const t = [];
    for (let v = yMin; v <= yMax; v += 10) t.push(v);
    return t;
  }, [yMin, yMax]);

  // X axis always spans the full session of the data's trading day.
  const session = useMemo(() => {
    if (!rows.length) return null;
    const dayStart = Math.floor((rows[0].Timestamp + IST_OFFSET_SEC) / 86400) * 86400 - IST_OFFSET_SEC;
    return {
      start: sessionStart || dayStart + SESSION_START_SEC,
      end: sessionEnd || dayStart + SESSION_END_SEC,
      dayStart,
    };
  }, [rows, sessionStart, sessionEnd]);

  // Half-hourly ticks (hourly for long MCX sessions), skipping the session-open label.
  const xTicks = useMemo(() => {
    if (!session) return [];
    const step = session.end - session.start > 7 * 3600 ? 3600 : 1800;
    const t = [];
    for (let ts = Math.floor((session.start + IST_OFFSET_SEC) / step + 1) * step - IST_OFFSET_SEC; ts <= session.end; ts += step) t.push(ts);
    return t;
  }, [session]);

  // Selected backtest time ("HH:MM" or "HH:MM:SS") → epoch on the same day.
  const markerTs = useMemo(() => {
    const m = String(markerTime || '').match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
    if (!session || !m) return null;
    const ts = session.dayStart + Number(m[1]) * 3600 + Number(m[2]) * 60 + Number(m[3] || 0);
    return ts >= session.start && ts <= session.end ? ts : null;
  }, [markerTime, session]);

  const plotTop = MARGIN.top;
  const plotBottom = CHART_HEIGHT - MARGIN.bottom - X_AXIS_HEIGHT;

  // Stack the right-axis badges (threshold + each line's last value) top-down, pushing
  // any badge that would overlap the one above it; returns the pixel shift per badge.
  const badgeShift = useMemo(() => {
    const pxPerUnit = (plotBottom - plotTop) / (yMax - yMin);
    const items = [{ id: 'threshold', value: THRESHOLD }];
    if (last) lines.forEach((s) => items.push({ id: s.id, value: last[s.key] ?? 0 }));
    const placed = items
      .map((it) => ({ ...it, y: plotTop + (yMax - it.value) * pxPerUnit }))
      .sort((a, b) => a.y - b.y);
    const shift = {};
    let prev = -Infinity;
    for (const it of placed) {
      const y = Math.max(it.y, prev + BADGE_GAP);
      shift[it.id] = y - it.y;
      prev = y;
    }
    return shift;
  }, [last, lines, yMin, yMax, plotTop, plotBottom]);

  // Mouse Y → value on the % axis, for the horizontal crosshair badge.
  const onMove = (state) => {
    if (!state?.isTooltipActive || state.activeLabel == null || state.chartY == null) return setHover(null);
    const ratio = Math.min(Math.max((state.chartY - plotTop) / (plotBottom - plotTop), 0), 1);
    setHover({ ts: state.activeLabel, value: yMax - ratio * (yMax - yMin) });
  };

  const [side, strike] = String(label).split('|').map((s) => s.trim());
  const watermark = side && strike ? `%${side} ${strike}` : '';

  return (
    <div style={{ background: C.bg, color: C.text, borderRadius: 8, overflow: 'hidden', fontFamily: FONT }}>
      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'center', background: C.header, padding: '8px 12px' }}>
        <div style={{ display: 'flex', gap: 8, width: 120 }}>
          <IconButton title="Chart view" active={view === 'chart'} onClick={() => setView('chart')}>
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 17 9 11 13 15 21 7" />
            </svg>
          </IconButton>
          <IconButton title="Table view" active={view === 'table'} onClick={() => setView('table')}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <rect x="3" y="3" width="18" height="4" rx="1" />
              <rect x="3" y="9" width="5" height="12" rx="1" />
              <rect x="9.5" y="9" width="5" height="12" rx="1" />
              <rect x="16" y="9" width="5" height="12" rx="1" />
            </svg>
          </IconButton>
        </div>
        <div style={{ flex: 1, textAlign: 'center', fontSize: 22, fontWeight: 500, letterSpacing: 0.3 }}>
          Percentage Graph
        </div>
        <div style={{ display: 'flex', gap: 8, width: 120, justifyContent: 'flex-end', alignItems: 'center' }}>
          <IconButton title="Each line is this strike's value as a % of the highest value among all strikes of the same type (CE/PE) at that time. OI change is scaled by the largest absolute change.">
            <svg width="24" height="24" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="11" fill="#d9d9d9" />
              <text x="12" y="17" textAnchor="middle" fontSize="15" fontWeight="700" fill={C.header}>?</text>
            </svg>
          </IconButton>
          <IconButton title="Close" data-bs-dismiss="modal" aria-label="Close">
            <svg width="22" height="22" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
              <line x1="5" y1="5" x2="19" y2="19" /><line x1="19" y1="5" x2="5" y2="19" />
            </svg>
          </IconButton>
        </div>
      </div>

      {/* ── Legend ── */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: 22, padding: '12px 0 4px' }}>
        {SERIES.map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => setVisible((v) => ({ ...v, [s.id]: !v[s.id] }))}
            style={{
              display: 'flex', alignItems: 'center', gap: 7, background: 'none', border: 'none',
              cursor: 'pointer', color: C.text, fontSize: 17, opacity: visible[s.id] ? 1 : 0.35, padding: 0,
            }}
          >
            <span style={{ width: 11, height: 11, borderRadius: '50%', background: s.color, display: 'inline-block' }} />
            {s.name}
          </button>
        ))}
      </div>

      {/* ── Body ── */}
      {loading ? (
        <p style={{ textAlign: 'center', padding: 80, color: C.muted, margin: 0 }}>Loading…</p>
      ) : !rows.length ? (
        <p style={{ textAlign: 'center', padding: 80, color: C.muted, margin: 0 }}>No data available</p>
      ) : view === 'table' ? (
        <div style={{ maxHeight: CHART_HEIGHT, overflowY: 'auto', padding: '8px 16px 16px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr style={{ position: 'sticky', top: 0, background: C.bg }}>
                <th style={{ textAlign: 'left', padding: '8px 10px', color: C.muted, fontWeight: 500 }}>Time</th>
                {SERIES.map((s) => (
                  <th key={s.id} style={{ textAlign: 'right', padding: '8px 10px', color: s.color, fontWeight: 600 }}>{s.name} %</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...rows].reverse().map((d) => (
                <tr key={d.Timestamp} style={{ borderTop: `1px solid ${C.grid}` }}>
                  <td style={{ padding: '6px 10px' }}>{fmtClock(d.Timestamp, true)}</td>
                  {SERIES.map((s) => (
                    <td key={s.id} style={{ textAlign: 'right', padding: '6px 10px', fontVariantNumeric: 'tabular-nums' }}>
                      {Number(d[s.key] ?? 0).toFixed(2)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ position: 'relative' }}>
          {watermark && (
            <div style={{
              position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'center', pointerEvents: 'none', color: '#2a2a2a', fontWeight: 800,
              fontSize: 48, lineHeight: 1.1, paddingRight: Y_AXIS_WIDTH, paddingBottom: X_AXIS_HEIGHT, userSelect: 'none',
            }}>
              <span>{watermark}</span>
            </div>
          )}
          <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
            <LineChart
              data={rows}
              margin={MARGIN}
              onMouseMove={onMove}
              onMouseLeave={() => setHover(null)}
            >
              <CartesianGrid stroke={C.grid} strokeDasharray="0" />
              <XAxis
                dataKey="Timestamp"
                type="number"
                scale="time"
                domain={session ? [session.start, session.end] : ['dataMin', 'dataMax']}
                allowDataOverflow
                ticks={xTicks}
                tick={<XTick />}
                tickLine={false}
                axisLine={{ stroke: C.grid }}
                height={X_AXIS_HEIGHT}
              />
              <YAxis
                orientation="right"
                domain={[yMin, yMax]}
                ticks={yTicks}
                allowDataOverflow
                tickFormatter={(v) => v.toFixed(2)}
                tick={{ fill: C.text, fontSize: 13 }}
                tickLine={false}
                axisLine={false}
                width={Y_AXIS_WIDTH}
              />

              {/* Threshold line */}
              <ReferenceLine
                y={THRESHOLD}
                stroke={C.threshold}
                strokeDasharray="6 4"
                strokeWidth={1.5}
                label={<AxisBadge value={THRESHOLD} fill={C.threshold} dy={badgeShift.threshold} />}
              />

              {/* Last value of each line: dotted guide + badge on the axis */}
              {last && lines.map((s) => (
                <ReferenceLine
                  key={`last-${s.id}`}
                  y={last[s.key]}
                  stroke={s.color}
                  strokeDasharray="2 3"
                  strokeWidth={1}
                  label={<AxisBadge value={last[s.key]} fill={s.color} dy={badgeShift[s.id]} />}
                />
              ))}

              {/* Selected backtest time */}
              {markerTs && (
                <ReferenceLine
                  x={markerTs}
                  stroke="#d9d9d9"
                  strokeDasharray="4 4"
                  strokeWidth={1}
                  label={{ value: fmtClock(markerTs, false), position: 'insideTopRight', fill: '#d9d9d9', fontSize: 12 }}
                />
              )}

              {/* Crosshair */}
              {hover && (
                <ReferenceLine
                  y={hover.value}
                  stroke="#9a9a9a"
                  strokeDasharray="2 3"
                  label={<AxisBadge value={hover.value} fill="#e6e6e6" textColor="#111" />}
                />
              )}
              {hover && (
                <ReferenceLine
                  x={hover.ts}
                  stroke="transparent"
                  label={<TimeBadge label={fmtClock(hover.ts, false)} />}
                />
              )}

              <Tooltip
                content={<HoverTooltip />}
                cursor={{ stroke: '#9a9a9a', strokeWidth: 1, strokeDasharray: '2 3' }}
                offset={24}
                isAnimationActive={false}
              />

              {lines.map((s) => (
                <Line
                  key={s.id}
                  type="stepAfter"
                  dataKey={s.key}
                  name={s.name}
                  stroke={s.color}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 5, fill: s.color, stroke: 'none' }}
                  isAnimationActive={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default OIPercentageGraph;

// Shared with OIChartsPanel so all OI Charts graphs look the same.
export {
  C, FONT, MARGIN, X_AXIS_HEIGHT, Y_AXIS_WIDTH, IST_OFFSET_SEC, BADGE_GAP,
  SESSION_START_SEC, SESSION_END_SEC, fmtClock, AxisBadge, TimeBadge, XTick, IconButton,
};
